import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'

interface GameState {
  score: number
  gameStarted: boolean
  isGameOver: boolean
  ballPosition: [number, number, number]
  swipeStart: { x: number; y: number } | null
  swipeEnd: { x: number; y: number } | null
  crossbarHit: boolean // Track if crossbar was hit
}

interface GameContextType {
  gameState: GameState
  incrementScore: () => void
  resetGame: () => void
  startGame: () => void
  endGame: () => void
  setBallPosition: (position: [number, number, number]) => void
  setSwipeStart: (position: { x: number; y: number } | null) => void
  setSwipeEnd: (position: { x: number; y: number } | null) => void
  calculateSwipeForce: () => { force: number; angle: number } | null
  setCrossbarHit: (hit: boolean) => void
  resetScore: () => void // <--- add this
}

const initialState: GameState = {
  score: 0,
  gameStarted: false,
  isGameOver: false,
  ballPosition: [0, 1, 0], // Ball at height 1 meter from ground
  swipeStart: null,
  swipeEnd: null,
  crossbarHit: false,
}

const GameContext = createContext<GameContextType | undefined>(undefined)

export const useGame = () => {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGame must be used within a GameProvider')
  }
  return context
}

interface GameProviderProps {
  children: ReactNode
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [gameState, setGameState] = useState<GameState>(initialState)

  const incrementScore = useCallback(() => {
    setGameState(prev => ({ ...prev, score: prev.score + 1 }))
  }, [])

  const resetScore = useCallback(() => {
    setGameState(prev => ({ ...prev, score: 0 }))
  }, [])

  const resetGame = useCallback(() => {
    setGameState({
      ...initialState,
      ballPosition: [0, 1, 0],
      gameStarted: true, // Start game immediately
      isGameOver: false,
      crossbarHit: false,
      swipeStart: null,
      swipeEnd: null,
    })
  }, [])

  const startGame = useCallback(() => {
    setGameState(prev => ({ ...prev, gameStarted: true, isGameOver: false }))
  }, [])

  const endGame = useCallback(() => {
    setGameState(prev => ({ ...prev, isGameOver: true }))
  }, [])

  const setBallPosition = useCallback((position: [number, number, number]) => {
    setGameState(prev => ({ ...prev, ballPosition: position }))
  }, [])

  const setSwipeStart = useCallback((position: { x: number; y: number } | null) => {
    setGameState(prev => ({ ...prev, swipeStart: position }))
  }, [])

  const setSwipeEnd = useCallback((position: { x: number; y: number } | null) => {
    setGameState(prev => ({ ...prev, swipeEnd: position }))
  }, [])

  const calculateSwipeForce = useCallback((): { force: number; angle: number } | null => {
    const { swipeStart, swipeEnd } = gameState
    
    if (!swipeStart || !swipeEnd) return null

    // Calculate distance and direction of swipe
    const deltaX = swipeEnd.x - swipeStart.x
    const deltaY = swipeEnd.y - swipeStart.y
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    
    // Normalize force (maximum 20, minimum 5)
    const force = Math.min(Math.max(distance / 10, 5), 20)
    
    // Calculate angle (negative deltaY because Y coordinates are inverted in browser)
    const angle = Math.atan2(-deltaY, deltaX)
    
    return { force, angle }
  }, [gameState.swipeStart, gameState.swipeEnd])

  const setCrossbarHit = useCallback((hit: boolean) => {
    setGameState(prev => ({ ...prev, crossbarHit: hit }))
  }, [])

  const contextValue: GameContextType = {
    gameState,
    incrementScore,
    resetGame,
    startGame,
    endGame,
    setBallPosition,
    setSwipeStart,
    setSwipeEnd,
    calculateSwipeForce,
    setCrossbarHit,
    resetScore,
  }

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  )
} 